import { useState, useRef, useEffect } from "react";
import { useSSRLayoutEffect } from "src/hooks/useSSRLayoutEffect";
import { cn } from "src/utils/cn";

type ReadMoreProps = {
  text: string;
  maxLines: number;
  textStyles?: string;
  toggleStyles?: string;
};

export default function ReadMore({
  text,
  maxLines,
  textStyles,
  toggleStyles,
}: ReadMoreProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  const checkShowToggle = () => {
    if (
      contentRef.current &&
      contentRef.current?.clientHeight < contentRef.current?.scrollHeight
    ) {
      setShowToggle(true);
    } else {
      setShowToggle(false);
    }
  };

  useSSRLayoutEffect(checkShowToggle, [contentRef, text, maxLines]);

  // The height of the component might have changed since the first time we
  // checked. Check again to see whether it still makes sense to show the "read
  // more" button.
  useEffect(() => {
    const onLoad = () => checkShowToggle();

    window.addEventListener("load", onLoad);

    return () => window.removeEventListener("load", onLoad);
  }, []);

  function onToggle() {
    setIsExpanded((prevState) => !prevState);
  }

  return (
    <div>
      <div
        ref={contentRef}
        className={cn(
          "break-word-fix",
          isExpanded ? "line-clamp-none" : "line-clamp-1",
          textStyles
        )}
        style={{ WebkitLineClamp: isExpanded ? "unset" : maxLines }}
      >
        {text}
      </div>
      <button
        className={cn(showToggle ? `text-blue-500` : `hidden`, toggleStyles)}
        onClick={onToggle}
      >
        {isExpanded ? "Read less" : "Read more"}
      </button>
    </div>
  );
}
